import { useCallback, useRef, useState } from 'react';

export type SecurityOperationKind = 'two-factor-irreversible' | 'session-revoke';

export interface SecurityOperation {
  token: number;
  kind: SecurityOperationKind;
}

export type AcquireSecurityOperation = (kind: SecurityOperationKind) => number | null;
export type ReleaseSecurityOperation = (token: number) => void;
export type CompleteCurrentSessionRevoke = (token: number) => void;
export type HasActiveSecurityOperation = (
  token?: number,
  expectedKind?: SecurityOperationKind,
) => boolean;

export const useSecurityOperationMutex = () => {
  const [operation, setOperation] = useState<SecurityOperation | null>(null);
  const operationRef = useRef<SecurityOperation | null>(null);
  const nextTokenRef = useRef(0);

  const acquireOperation = useCallback<AcquireSecurityOperation>((kind) => {
    if (operationRef.current) return null;

    const nextOperation = { token: ++nextTokenRef.current, kind };
    operationRef.current = nextOperation;
    setOperation(nextOperation);
    return nextOperation.token;
  }, []);

  const consumeOperation = useCallback((token: number, expectedKind?: SecurityOperationKind) => {
    const current = operationRef.current;
    if (!current || current.token !== token || (expectedKind && current.kind !== expectedKind)) {
      return false;
    }

    operationRef.current = null;
    setOperation((visible) => visible?.token === token ? null : visible);
    return true;
  }, []);

  const releaseOperation = useCallback<ReleaseSecurityOperation>((token) => {
    consumeOperation(token);
  }, [consumeOperation]);

  const hasActiveOperation = useCallback<HasActiveSecurityOperation>((token, expectedKind) => {
    const current = operationRef.current;
    if (!current) return false;
    if (token !== undefined && current.token !== token) return false;
    return expectedKind === undefined || current.kind === expectedKind;
  }, []);

  return {
    operation,
    acquireOperation,
    releaseOperation,
    consumeOperation,
    hasActiveOperation,
  };
};
